import { ClipbookState, PagePicture, Page } from '../types';
import {
  JSONReadyDrawingData,
  collectPictureIDsFromJSONReadyDrawingData,
  jsonizeDrawingData,
} from '../../drawing/reducers/drawing';
import { EntireComposerState } from './composer';
import { applyModification } from '../../reducers/autoSaving';
import {
  preparePictureItemsForSaving,
  PictureStorageState,
  isPictureSavingFinished,
  finalizePictureItemsForSaving,
} from '../../drawing/reducers/pictureStorage';

export interface JSONReadyClipbookData {
  readonly name: string;
  readonly pages: ReadonlyArray<Page>;
  readonly currentPage: number | null;
  readonly drawings: { readonly [id: string]: JSONReadyDrawingData };
}

function createIsPagePictureToBeSaved(clipbook: ClipbookState) {
  const ids: { [id: string]: true } = {};

  for (const page of clipbook.pages) {
    const { backgroundID } = page;
    ids[backgroundID] = true;
  }

  return (id: string) => id in ids;
}

function createIsDrawingPictureToBeSaved(
  drawings: { readonly [pageID: string]: JSONReadyDrawingData },
) {
  const ids: { [id: string]: true } = {};

  for (const pageID in drawings) {
    for (const id of collectPictureIDsFromJSONReadyDrawingData(drawings[pageID])) {
      ids[id] = true;
    }
  }

  return (id: string) => id in ids;
}

export function applyClipbookModification(state: EntireComposerState): EntireComposerState {
  const nextAutoSaving = applyModification(state.autoSaving);
  return (state.autoSaving === nextAutoSaving) ? state : { ...state, autoSaving: nextAutoSaving };
}

export function prepareClipbookSaving(state: EntireComposerState): EntireComposerState {
  const { clipbook } = state;
  if (clipbook === null || state.savingClipbook !== null) {
    throw new Error('Illegal state.');
  }

  const drawings: { [pageID: string]: JSONReadyDrawingData } = {};
  for (const pageID in state.drawings) {
    drawings[pageID] = jsonizeDrawingData(state.drawings[pageID]);
  }

  return {
    ...state,
    savingClipbook: {
      id: clipbook.id,
      body: {
        drawings,
        name: clipbook.name,
        pages: clipbook.pages,
        currentPage: clipbook.currentPage,
      },
    },
    pagePictures: preparePictureItemsForSaving(
      state.pagePictures,
      createIsPagePictureToBeSaved(clipbook),
    ),
    drawingPictures: preparePictureItemsForSaving(
      state.drawingPictures,
      createIsDrawingPictureToBeSaved(drawings),
    ),
  };
}

export function isReadyToSaveClipbook(
  savingClipbook: { id: string, body: JSONReadyClipbookData } | null,
  pagePictures: PictureStorageState<PagePicture>,
  drawingPictures: PictureStorageState<HTMLCanvasElement>,
) {
  if (savingClipbook === null) {
    // So that !isReadyToSaveClipbook(prev) && isReadyToSaveClipbook(current) triggers saving,
    return false;
  }

  return isPictureSavingFinished(pagePictures) && isPictureSavingFinished(drawingPictures);
}

export function startClipbookSaving(state: EntireComposerState): EntireComposerState {
  if (state.savingClipbook === null) {
    throw new Error('Illegal state.');
  }

  return {
    ...state,
    pagePictures: finalizePictureItemsForSaving(state.pagePictures),
    drawingPictures: finalizePictureItemsForSaving(state.drawingPictures),
  };
}
